import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom/client';

import { message, notification } from 'antd';
import { SoundOutlined, AlertOutlined, ToolOutlined, CheckCircleOutlined } from '@ant-design/icons';
import i18n from '@/i18n/i18n';

// 翻譯訊息（如果是 i18n key 則翻譯，否則直接顯示）
const translateMessage = (msg, params = {}) => {
  if (!msg) return '';
  // 檢查是否為 i18n key 格式（包含點號且不含空格）
  if (msg.includes('.') && !msg.includes(' ')) {
    return i18n.t(msg, params);
  }
  return msg;
};

// ========== 自訂通知組件 ==========
const BroadcastNotification = ({ type, title, content, sender, onClose }) => {
  const isUrgent = type === 'urgent';

  const colors = {
    urgent: { primary: '#ff3b30', bg: 'rgba(255, 59, 48, 0.12)' },
    announcement: { primary: '#007aff', bg: 'rgba(0, 122, 255, 0.1)' },
    task: { primary: '#ff9500', bg: 'rgba(255, 149, 0, 0.1)' },
    task_completed: { primary: '#34c759', bg: 'rgba(52, 199, 89, 0.1)' },
  };

  const color = colors[type] || colors.announcement;

  const IconComponent = {
    urgent: AlertOutlined,
    announcement: SoundOutlined,
    task: ToolOutlined,
    task_completed: CheckCircleOutlined,
  }[type] || SoundOutlined;

  return (
    <div style={{
      position: 'fixed',
      top: isUrgent ? '50%' : '20px',
      left: '50%',
      transform: isUrgent ? 'translate(-50%, -50%)' : 'translateX(-50%)',
      zIndex: 10000,
      animation: 'notificationIn 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
    }}>
      <style>{`
        @keyframes notificationIn {
          from {
            opacity: 0;
            transform: ${isUrgent ? 'translate(-50%, -50%) scale(0.9)' : 'translate(-50%, -20px)'};
          }
          to {
            opacity: 1;
            transform: ${isUrgent ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, 0)'};
          }
        }
      `}</style>

      <div style={{
        background: isUrgent
          ? 'linear-gradient(180deg, rgba(60, 20, 20, 0.75) 0%, rgba(40, 15, 15, 0.75) 100%)'
          : 'rgba(40, 40, 45, 0.65)',
        backdropFilter: 'blur(50px)',
        WebkitBackdropFilter: 'blur(50px)',
        borderRadius: isUrgent ? '20px' : '16px',
        boxShadow: isUrgent
          ? '0 25px 50px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 59, 48, 0.4)'
          : '0 20px 40px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.15)',
        padding: isUrgent ? '20px 24px' : '14px 18px',
        minWidth: isUrgent ? '360px' : '320px',
        maxWidth: '420px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '14px',
        }}>
          {/* Icon */}
          <div style={{
            width: isUrgent ? '44px' : '38px',
            height: isUrgent ? '44px' : '38px',
            borderRadius: isUrgent ? '12px' : '10px',
            background: color.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IconComponent style={{
              fontSize: isUrgent ? '22px' : '18px',
              color: color.primary,
            }} />
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '6px',
            }}>
              <span style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.6)',
                letterSpacing: '0.3px',
              }}>
                {title}
              </span>
              <span style={{
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.35)',
              }}>
                {new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>

            {/* Message */}
            <div style={{
              fontSize: isUrgent ? '16px' : '15px',
              fontWeight: isUrgent ? 500 : 400,
              color: '#ffffff',
              lineHeight: 1.5,
              marginBottom: '8px',
              wordBreak: 'break-word',
            }}>
              {content}
            </div>

            {/* Sender */}
            <div style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.4)',
            }}>
              {sender}
            </div>
          </div>

          {/* Close */}
          <div
            onClick={onClose}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            <span style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.5)',
              lineHeight: 1,
            }}>✕</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// 顯示自訂通知
const showBroadcastNotification = (type, title, content, sender, duration = 6000) => {
  const container = document.createElement('div');
  container.id = `broadcast-notification-${Date.now()}`;
  document.body.appendChild(container);

  const root = ReactDOM.createRoot(container);

  const close = () => {
    const el = container.firstChild;
    if (el) {
      el.style.opacity = '0';
      el.style.transform = type === 'urgent' ? 'translate(-50%, -50%) scale(0.95)' : 'translate(-50%, -10px)';
      el.style.transition = 'all 0.25s ease';
    }
    setTimeout(() => {
      root.unmount();
      container.remove();
    }, 250);
  };

  root.render(
    <BroadcastNotification
      type={type}
      title={title}
      content={content}
      sender={sender}
      onClose={close}
    />
  );

  // 自動關閉（緊急通知不自動關閉）
  if (type !== 'urgent' && duration > 0) {
    setTimeout(close, duration);
  }

  return close;
};

import operatorCable from '@/lib/operatorCable';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';

// 廣播類型配置
const BROADCAST_TYPE_CONFIG = {
  announcement: { icon: SoundOutlined, color: '#1890ff', title: '公告' },
  task: { icon: ToolOutlined, color: '#fa8c16', title: '任務' },
  task_completed: { icon: CheckCircleOutlined, color: '#52c41a', title: '任務完成' },
  urgent: { icon: AlertOutlined, color: '#ff4d4f', title: '緊急通知' },
};

/**
 * Operator 專用的即時通知 Hook
 *
 * 功能：
 * - 自動訂閱 NotificationChannel（統一的通知頻道）
 * - 接收 job 狀態更新和系統通知
 * - 顯示 Ant Design message 提示
 * - 自動清理訂閱（防止 fd 洩漏）
 *
 * @returns {Object} { notificationData, clearJobStatus, setModalVisible }
 *
 * 使用範例：
 * ```javascript
 * function OperatorImportPage() {
 *   const { notificationData } = useNotification();
 *
 *   const handleImport = async () => {
 *     const res = await operatorApiFetch('/operator_api/v1/import', { method: 'POST' });
 *     message.info(`任務已開始 (Job ID: ${res.data.job_id})`);
 *   };
 *
 *   return <Button onClick={handleImport}>開始匯入</Button>;
 * }
 * ```
 */
// 進度通知節流：long-running worker 會以 ~25 Hz 推送 progress（broadcast_progress），
// 每筆都進 setNotificationData → BackgroundJobContext 值變動 → 整個 operator 子樹高頻 re-render。
// dev StrictMode 雙倒 render 下,某些下游（如 Table 分頁 Select 的 inline options/component）會在
// 一個 commit cycle 內疊到 React 巢狀 setState 上限,丟 Maximum update depth exceeded。
// 對「非終止狀態」事件 coalesce per 100ms by job_id（最後一筆贏）；
// 終止狀態（completed/failed）即時寫入並先 flush 暫存,保證最終狀態看到正確值。
const TERMINAL_JOB_STATUSES = new Set([ 'completed', 'failed' ]);

export function useNotification() {
  const { isAuthenticated } = useOperatorAuth();
  const [notificationData, setNotificationData] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const modalVisibleRef = useRef(false);

  // 節流用 buffer + timer
  const pendingProgressRef = useRef({});
  const flushTimerRef = useRef(null);

  const flushPendingProgress = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const pending = pendingProgressRef.current;
    if (Object.keys(pending).length === 0) return;
    pendingProgressRef.current = {};
    setNotificationData((prev) => ({ ...prev, ...pending }));
  }, []);

  // 🔥 [SessionStorage] 使用 sessionStorage 確保 Reload 後仍記得是此 Tab 發起的
  const JOB_OWNERSHIP_KEY = 'gw_operator_my_jobs';

  const getMyJobs = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(JOB_OWNERSHIP_KEY);
      return new Set(stored ? JSON.parse(stored) : []);
    } catch (e) {
      console.warn('Failed to parse job ownership', e);
      return new Set();
    }
  }, []);

  const addMyJob = useCallback((jobId) => {
    try {
      const current = getMyJobs();
      current.add(String(jobId));
      // 限制數量避免無限膨脹，保留最近 50 筆
      const limited = Array.from(current).slice(-50);
      sessionStorage.setItem(JOB_OWNERSHIP_KEY, JSON.stringify(limited));
    } catch (e) {
      console.warn('Failed to save job ownership', e);
    }
  }, [getMyJobs]);

  /**
   * 註冊一個 Job ID 為「本 Tab 發起」
   * 在呼叫 API 成功取得 job_id 後立即呼叫此方法
   */
  const registerJob = useCallback((jobId) => {
    if (jobId) {
      console.log('[Operator useNotification] 🏷️ Registered job ownership (Persisted):', jobId);
      addMyJob(jobId);
    }
  }, [addMyJob]);

  // 同步 modalVisible 到 ref
  useEffect(() => {
    modalVisibleRef.current = modalVisible;
  }, [modalVisible]);

  useEffect(() => {
    // ✅ 未登入時不訂閱 WebSocket，避免登入頁面發起無效連線
    if (!isAuthenticated) return;

    console.log('[Operator useNotification] 🚀 Subscribing to NotificationChannel on mount');

    // 訂閱 Operators::NotificationChannel
    // ✅ 一進入後台就訂閱，不管 Modal 是否打開
    const unsubscribe = operatorCable.subscribe(
      'NotificationChannel',
      {},
      {
        received: (data) => {
          console.log('[Operator useNotification] 📥 Received WebSocket Data:', JSON.stringify(data, null, 2));

          // ✅ 根據 type 處理不同類型的訊息
          if (data.type === 'job_status') {
            // [修正] 從 SessionStorage 讀取判斷
            const myJobs = getMyJobs();
            const isOriginator = myJobs.has(String(data.job_id));
            const enrichedData = { ...data, isOriginator };

            console.log(`[Operator useNotification] ⚙️ Job Status Update: ${data.job_id} -> ${data.status} (Originator: ${isOriginator})`);

            // Background Job 狀態更新 — 節流見頂部 TERMINAL_JOB_STATUSES 註解。
            if (TERMINAL_JOB_STATUSES.has(data.status)) {
              // 終止狀態優先：先 flush 之前 coalesce 的 progress（確保 progress final 值不會壓過 completed/failed），
              // 再把終止資料寫入。flush+set 兩次 setState 會被 React 自動 batch 成一個 commit。
              flushPendingProgress();
              setNotificationData((prev) => ({ ...prev, [data.job_id]: enrichedData }));
            } else {
              // processing / started 等非終止狀態：100ms coalesce by job_id（最後一筆贏）
              pendingProgressRef.current[data.job_id] = enrichedData;
              if (!flushTimerRef.current) {
                flushTimerRef.current = setTimeout(flushPendingProgress, 100);
              }
            }

            // 只在 Modal 關閉時才顯示通知
            if (!modalVisibleRef.current) {
              console.log(`[Operator useNotification] 🔔 Modal hidden, showing notification for ${data.status}`);
              switch (data.status) {
                case 'completed':
                  // 匯入完成由 BackgroundJobManager 用 notification.success() 顯示
                  if (!data.data?.import_id) {
                    message.success(translateMessage(data.message, data.progress) || i18n.t('background_job.messages.complete'));
                  }
                  break;
                case 'failed':
                  // 失敗由 BackgroundJobManager 用 notification.error() 顯示（持久通知）
                  break;
                case 'started':
                case 'processing':
                  console.log('[Operator useNotification] Job Progress:', data.job_id, data.message, data.progress);
                  break;
                default:
                  console.log('[Operator useNotification] Unknown job status:', data.status);
              }
            } else {
              console.log('[Operator useNotification] 💤 Modal visible, skipping message toast');
            }
          } else if (data.type === 'new_notification') {
            // 系統通知（Notification）
            // 這個會由 NotificationBell 組件處理
            console.log('[Operator useNotification] 📬 New notification:', data.notification);
            setNotificationData((prev) => ({
              ...prev,
              [`notification_${Date.now()}`]: data
            }));
          } else if ((data.target === 'warehouse' || data.target === 'merchant') && BROADCAST_TYPE_CONFIG[data.type]) {
            // 🔊 廣播訊息（倉庫或貨主）
            console.log('[Operator useNotification] 📢 Broadcast:', data);

            const config = BROADCAST_TYPE_CONFIG[data.type];
            const messageContent = data.message?.content || '收到新的廣播訊息';
            const senderName = data.message?.sender_name || '系統';

            // 顯示通知
            showBroadcastNotification(
              data.type,
              config.title,
              messageContent,
              senderName
            );

            // 存儲廣播訊息
            setNotificationData((prev) => ({
              ...prev,
              [`broadcast_${Date.now()}`]: data
            }));
          }
        },
        connected: () => {
          console.log('[Operator useNotification] ✅ Connected to NotificationChannel');
        },
        disconnected: () => {
          console.log('[Operator useNotification] ⚠️ Disconnected from NotificationChannel');
          // 🔥 不需要手動重連，ActionCable 會自動處理
          // 當連接恢復時，connected() 會被再次觸發
        },
        rejected: () => {
          console.error('[Operator useNotification] ❌ Subscription rejected');
          message.error('無法連接到通知服務');
        }
      }
    );

    // ✅ Cleanup：組件 unmount 時清理訂閱 + 節流計時器（避免 unmount 後仍 setState）
    return () => {
      console.log('[Operator useNotification] 🧹 Cleanup: Unsubscribing from NotificationChannel');
      unsubscribe();
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      // 與 admin 對齊：unmount / 重訂閱時清掉 pending buffer，避免殘留 progress 被下個訂閱 flush
      pendingProgressRef.current = {};
    };
  }, [isAuthenticated, getMyJobs, flushPendingProgress]); // ✅ 加入 isAuthenticated 依賴，未登入時跳過訂閱；flushPendingProgress 為 stable useCallback 不會觸發 re-subscribe

  const clearJobStatus = useCallback((jobId) => {
    setNotificationData((prev) => {
      const newStatus = { ...prev };
      delete newStatus[jobId];
      return newStatus;
    });
  }, []);

  return {
    notificationData,  // ✅ 包含所有通知類型（job_status + new_notification）
    clearJobStatus,
    setModalVisible,  // ✅ 返回 setModalVisible 讓外部控制是否顯示通知
    registerJob       // ✅ [新增] 匯出註冊函數
  };
}

/**
 * Operator 專用的單一 Job 監聽 Hook
 *
 * @param {string|number} jobId - Job ID
 * @param {Object} callbacks - { onCompleted, onFailed, onProgress }
 */
export function useSingleNotification(jobId, { onCompleted, onFailed, onProgress } = {}) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!jobId) return;

    console.log('[Operator useSingleNotification] 🚀 Subscribing for jobId:', jobId);

    // ✅ 不需要 role 參數
    const unsubscribe = operatorCable.subscribe(
      'NotificationChannel',
      {},
      {
        received: (data) => {
          // 只處理 job_status 類型的訊息
          if (data.type !== 'job_status') return;

          // 只處理匹配的 job_id
          if (data.job_id !== jobId && data.job_id !== String(jobId)) return;

          console.log('[Operator useSingleNotification] ✅ Received data for jobId:', jobId, data);
          setStatus(data);

          switch (data.status) {
            case 'completed':
              if (onCompleted) onCompleted(data);
              break;
            case 'failed':
              if (onFailed) onFailed(data);
              break;
            case 'processing':
              if (onProgress) onProgress(data);
              break;
          }
        }
      }
    );

    // ✅ Cleanup：組件 unmount 時清理訂閱
    return () => {
      console.log('[Operator useSingleNotification] 🧹 Cleanup: Unsubscribing for jobId:', jobId);
      unsubscribe();
    };
  }, [jobId, onCompleted, onFailed, onProgress]);

  return status;
}
